import React, { useState, useEffect } from "react";
import { Link as ReactRouterLink } from "react-router-dom";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tooltip,
  Button,
  CircularProgress,
} from "@mui/material";
import { format } from "date-fns";
import { getQuotes } from "../../../services/quotes.service";
import { Quote as ApiQuote } from "../../../models/quote.model";
import { useAppContext } from "../../../utils/content.util";
import { onError } from "../../../utils/error.util";

interface Quote {
  id: string;
  date: string;
  lastName: string;
  vinNumber: string;
  county: string;
  cityStateZip: string;
  status?: string;
}

function QuoteTable({
  data,
  page,
  rowsPerPage = 300,
  handleChangeRowsPerPage,
  handleChangePage,
}: any) {
  return (
    <React.Fragment>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell> </TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Date</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Last Name</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>VIN Number</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Location</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row: any) => (
              <QuoteTableRow key={row.id} row={row} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[300]}
        component="div"
        count={data.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </React.Fragment>
  );
}

interface QuoteTableRowProps {
  row: Quote;
}

function QuoteTableRow({ row }: QuoteTableRowProps) {
  return (
    <TableRow>
      <TableCell>
        <Tooltip title="Edit">
          <ReactRouterLink key={row.id} to={`/quotes/${row.id}`}>
            <VisibilityIcon color="primary" fontSize="small" />
          </ReactRouterLink>
        </Tooltip>
      </TableCell>
      <TableCell>
        {format(new Date(row.date), "MM-dd-yyyy")}
        <br />
        {format(new Date(row.date), "p")}
      </TableCell>
      <TableCell>{row.lastName}</TableCell>
      <TableCell>{row.vinNumber}</TableCell>
      <TableCell>
        <Box>
          <Typography sx={{ fontWeight: "bold" }}>{row.county}</Typography>
          <Typography variant="body2">{row.cityStateZip}</Typography>
        </Box>
      </TableCell>
      <TableCell>{row.status || "N/A"}</TableCell>
    </TableRow>
  );
}

export default function QuotesContainer() {
  const { isAuthenticated } = useAppContext();
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(300);

  const mapApiQuoteToDisplay = (apiQuote: ApiQuote): Quote => {
    const cityStateZip = [apiQuote.city, apiQuote.titleState, apiQuote.titleZip]
      .filter(Boolean)
      .join(", ")
      .trim();
    return {
      id: apiQuote.id,
      date: apiQuote.date,
      lastName: apiQuote.driverLastName,
      vinNumber: apiQuote.vinNumber,
      county: apiQuote.county,
      cityStateZip: cityStateZip || "N/A",
      status: apiQuote.status,
    };
  };

  useEffect(() => {
    const loadQuotes = async () => {
      if (!isAuthenticated) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const response = await getQuotes({ limit: 300 });
        const mappedQuotes = response.items.map(mapApiQuoteToDisplay);
        setQuotes(mappedQuotes);
      } catch (e) {
        onError(e);
      } finally {
        setLoading(false);
      }
    };

    loadQuotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      <div className="quotes">
        <h2 className="pb-2 mt-4 mb-3 border-bottom">Quotes</h2>
      </div>
      <div>
        <div className="pb-2 mt-4 mb-3">
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              width: "100%",
              gap: 1,
            }}
          >
            <ReactRouterLink to={"/quotes/new"}>
              <Button variant="contained">New Quote</Button>
            </ReactRouterLink>
          </Box>
        </div>
      </div>

      <QuoteTable
        data={quotes}
        page={page}
        rowsPerPage={rowsPerPage}
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
      />
    </Box>
  );
}
